import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MdAlarm } from "react-icons/md";
import { installAudioUnlock, startAlarm } from "../utils/alertSound";
import { CheckoutDueAlert } from "../hooks/useCheckoutDueAlerts";

interface CheckoutDuePopupProps {
  alerts: CheckoutDueAlert[];
  onAcknowledge: () => void;
}

/** Full-attention alert for rooms whose check-out time has come. It rings until acknowledged. */
const CheckoutDuePopup: React.FC<CheckoutDuePopupProps> = ({ alerts, onAcknowledge }) => {
  const navigate = useNavigate();

  useEffect(() => {
    installAudioUnlock();
    return startAlarm();
  }, []);

  const openBookings = () => {
    onAcknowledge();
    navigate("/admin/bookings");
  };

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 p-4 animate-overlay-fade-in"
    >
      <div className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl ring-4 ring-amber-500 animate-modal-pop-in">
        <div className="flex items-center gap-4 bg-amber-500 px-6 py-5 text-white animate-pulse">
          <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white/20 text-5xl">
            <MdAlarm />
          </span>
          <div>
            <h3 className="text-3xl font-extrabold uppercase tracking-wide">Check-out due</h3>
            <p className="text-sm text-amber-50">
              {alerts.length === 1
                ? "A guest has reached their check-out time"
                : `${alerts.length} guests have reached their check-out time`}
            </p>
          </div>
        </div>

        <div className="max-h-[50vh] space-y-3 overflow-y-auto px-6 py-5">
          {alerts.map((r) => (
            <div
              key={r.roomBookingId}
              className="rounded-2xl border-2 border-amber-200 bg-amber-50 px-5 py-4"
            >
              <p className="text-4xl font-extrabold text-amber-700">Room {r.roomNo ?? r.roomBookingId}</p>
              <p className="truncate text-sm text-gray-600">
                {r.roomTypeName ?? ""}
                {r.guestName ? ` · ${r.guestName}` : ""}
              </p>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
          <button
            onClick={onAcknowledge}
            className="rounded-xl bg-gray-100 px-5 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
          >
            Acknowledge
          </button>
          <button
            onClick={openBookings}
            className="rounded-xl bg-amber-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-amber-700"
          >
            Open Bookings
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutDuePopup;
